'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { PowerBarMarker } from '@/domains/pages/shared/power-bar-marker';
import { powerBarColoredFillStyle, POWER_BAR_TRACK_CLASS, powerBarMarkerPosition, powerBarLayoutBottomSpacerPx, selectedValueTone, STANDARD_POWER_BAR_LAYOUT } from '@/domains/pages/shared/power-bar-shared';
import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { isAheadOfLevelBenchmark } from '@/domains/pages/ai-proficiency/utils/ladder';
import { cn } from '@/utils/shad';
import { formatScore } from '@/utils/format.util';
import { useCallback, useRef, useState } from 'react';
type LadderComparisonMode = 'topQuartile' | 'bestSegment';

/** Every group bar reads on the same 0-100% share scale, so columns stay comparable. */
const LADDER_BAR_MAX_PERCENT = 100;

/** Keeps a small non-zero share visible instead of collapsing to a hairline. */
const LADDER_BAR_MIN_FILL_PX = 3;
type LadderBarTooltipContext = {
  orgSharePercent?: number;
  topQuartileShare?: number;
  bestSegmentShare: number;
  bestSegmentLabel: string;
};
type LadderLevelBarProps = {
  level: AifiLevel;
  sharePercent: number;
  benchmarkShare: number;
  fillColor: string;
  comparison: LadderComparisonMode;
  subjectLabel: string;
  segmentNoun?: string;
  barTooltip: LadderBarTooltipContext;
  className?: string;
  showBenchmark?: boolean;
};
type LadderBarHoverTarget = 'bar' | 'benchmark';
function buildBenchmarkTooltipLabel(level: AifiLevel, comparison: LadderComparisonMode, segmentNoun?: string): string {
  if (comparison === 'bestSegment') {
    return `Best ${segmentNoun ?? 'segment'} at ${level}`;
  }
  return `Top quartile at ${level}`;
}

/** Signed share gap, in the same percentage-point unit as the bars. */
function formatShareDiff(diff: number): string {
  return `${diff >= 0 ? '+' : ''}${formatScore(diff, {
    decimals: 1
  })}%`;
}
export function LadderLevelBar(t0) {
  const $ = _c(84);
  const {
    level,
    sharePercent,
    benchmarkShare,
    fillColor,
    comparison,
    subjectLabel,
    segmentNoun,
    barTooltip,
    className,
    showBenchmark: t1
  } = t0;
  const showBenchmark = t1 === undefined ? true : t1;
  const barRef = useRef(null);
  const [hoverTarget, setHoverTarget] = useState(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 0,
      y: 0
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [tooltipPos, setTooltipPos] = useState(t2);
  const fillRatio = sharePercent / LADDER_BAR_MAX_PERCENT;
  const benchmarkRatio = benchmarkShare / LADDER_BAR_MAX_PERCENT;
  const shareDiff = sharePercent - benchmarkShare;
  let barLit;
  let barTopPx;
  let benchmarkLit;
  let hideTooltip;
  let showBarHover;
  let showBenchmarkHover;
  let t3;
  if ($[1] !== barTooltip.bestSegmentLabel || $[2] !== barTooltip.bestSegmentShare || $[3] !== barTooltip.orgSharePercent || $[4] !== barTooltip.topQuartileShare || $[5] !== benchmarkRatio || $[6] !== benchmarkShare || $[7] !== comparison || $[8] !== fillColor || $[9] !== fillRatio || $[10] !== hoverTarget || $[11] !== level || $[12] !== segmentNoun || $[13] !== shareDiff || $[14] !== sharePercent || $[15] !== subjectLabel) {
    const ahead = isAheadOfLevelBenchmark(level, sharePercent, benchmarkShare);
    barTopPx = STANDARD_POWER_BAR_LAYOUT.trackTopPx;
    let t4;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = ratio => {
        const bar = barRef.current;
        if (!bar) {
          return;
        }
        const rect = bar.getBoundingClientRect();
        setTooltipPos({
          x: rect.left + rect.width * Math.min(1, Math.max(0, ratio)),
          y: rect.top
        });
      };
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    const updateTooltipAtRatio = t4;
    let t5;
    if ($[24] !== fillRatio) {
      t5 = () => {
        updateTooltipAtRatio(fillRatio);
        setHoverTarget("bar");
      };
      $[24] = fillRatio;
      $[25] = t5;
    } else {
      t5 = $[25];
    }
    showBarHover = t5;
    let t6;
    if ($[26] !== benchmarkRatio) {
      t6 = () => {
        updateTooltipAtRatio(benchmarkRatio);
        setHoverTarget("benchmark");
      };
      $[26] = benchmarkRatio;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    showBenchmarkHover = t6;
    let t7;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => setHoverTarget(null);
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    hideTooltip = t7;
    barLit = hoverTarget === "bar";
    benchmarkLit = hoverTarget === "benchmark";
    const comparisonLabel = comparison === "bestSegment" ? "Org" : "Top quartile";
    const comparisonShare = comparison === "bestSegment" ? barTooltip.orgSharePercent ?? 0 : barTooltip.topQuartileShare ?? 0;
    t3 = hoverTarget === "bar" ? [{
      label: `${level} share`,
      value: `${formatScore(sharePercent, {
        decimals: 1
      })}%`,
      valueStyle: {
        color: fillColor
      }
    }, {
      label: comparisonLabel,
      value: `${formatScore(comparisonShare, {
        decimals: 1
      })}%`
    }, {
      label: barTooltip.bestSegmentLabel,
      value: `${formatScore(barTooltip.bestSegmentShare, {
        decimals: 1
      })}%`
    }, {
      label: `vs. ${comparisonLabel.toLowerCase()}`,
      value: formatShareDiff(sharePercent - comparisonShare),
      ...selectedValueTone(isAheadOfLevelBenchmark(level, sharePercent, comparisonShare))
    }, {
      label: `vs. ${barTooltip.bestSegmentLabel.toLowerCase()}`,
      value: formatShareDiff(sharePercent - barTooltip.bestSegmentShare),
      ...selectedValueTone(isAheadOfLevelBenchmark(level, sharePercent, barTooltip.bestSegmentShare))
    }] : hoverTarget === "benchmark" ? [{
      label: buildBenchmarkTooltipLabel(level, comparison, segmentNoun),
      value: `${formatScore(benchmarkShare, {
        decimals: 1
      })}%`
    }, {
      label: subjectLabel,
      value: `${formatScore(sharePercent, {
        decimals: 1
      })}%`,
      ...selectedValueTone(ahead)
    }, {
      label: "Difference",
      value: `${shareDiff >= 0 ? "+" : ""}${formatScore(shareDiff, {
        decimals: 1
      })}%`,
      ...selectedValueTone(ahead)
    }] : [];
    $[1] = barTooltip.bestSegmentLabel;
    $[2] = barTooltip.bestSegmentShare;
    $[3] = barTooltip.orgSharePercent;
    $[4] = barTooltip.topQuartileShare;
    $[5] = benchmarkRatio;
    $[6] = benchmarkShare;
    $[7] = comparison;
    $[8] = fillColor;
    $[9] = fillRatio;
    $[10] = hoverTarget;
    $[11] = level;
    $[12] = segmentNoun;
    $[13] = shareDiff;
    $[14] = sharePercent;
    $[15] = subjectLabel;
    $[16] = barLit;
    $[17] = barTopPx;
    $[18] = benchmarkLit;
    $[19] = hideTooltip;
    $[20] = showBarHover;
    $[21] = showBenchmarkHover;
    $[22] = t3;
  } else {
    barLit = $[16];
    barTopPx = $[17];
    benchmarkLit = $[18];
    hideTooltip = $[19];
    showBarHover = $[20];
    showBenchmarkHover = $[21];
    t3 = $[22];
  }
  const tooltipRows = t3;
  let t4;
  if ($[29] !== className) {
    t4 = cn("relative", className);
    $[29] = className;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  const t5 = barLit && "brightness-110";
  let t6;
  if ($[31] !== t5) {
    t6 = cn("absolute right-0 left-0 cursor-pointer transition-[filter] duration-150", t5);
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== barTopPx) {
    t7 = {
      top: barTopPx,
      height: `${STANDARD_POWER_BAR_LAYOUT.trackHeightPx}px`
    };
    $[33] = barTopPx;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  const t8 = barLit && "brightness-105";
  let t9;
  if ($[35] !== t8) {
    t9 = cn("absolute inset-0 transition-[filter] duration-150", POWER_BAR_TRACK_CLASS, t8);
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== t9) {
    t10 = <div className={t9} />;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const t11 = barLit && "brightness-110";
  let t12;
  if ($[39] !== t11) {
    t12 = cn("absolute inset-y-0 left-0 transition-[filter] duration-150", t11);
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== sharePercent) {
    t13 = powerBarMarkerPosition(sharePercent, LADDER_BAR_MAX_PERCENT);
    $[41] = sharePercent;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const t14 = sharePercent > 0 ? LADDER_BAR_MIN_FILL_PX : 0;
  let t15;
  if ($[43] !== fillColor) {
    t15 = powerBarColoredFillStyle(fillColor);
    $[43] = fillColor;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t13 || $[46] !== t14 || $[47] !== t15) {
    t16 = {
      width: t13,
      minWidth: t14,
      ...t15
    };
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t12 || $[50] !== t16) {
    t17 = <div className={t12} style={t16} />;
    $[49] = t12;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== showBarHover || $[53] !== t10 || $[54] !== t17 || $[55] !== t6 || $[56] !== t7) {
    t18 = <div className={t6} style={t7} onMouseEnter={showBarHover}>{t10}{t17}</div>;
    $[52] = showBarHover;
    $[53] = t10;
    $[54] = t17;
    $[55] = t6;
    $[56] = t7;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== benchmarkLit || $[59] !== benchmarkShare || $[60] !== comparison || $[61] !== hideTooltip || $[62] !== level || $[63] !== segmentNoun || $[64] !== showBenchmark || $[65] !== showBenchmarkHover) {
    t19 = showBenchmark ? <PowerBarMarker layout={STANDARD_POWER_BAR_LAYOUT} position={powerBarMarkerPosition(benchmarkShare, LADDER_BAR_MAX_PERCENT)} lit={benchmarkLit} ariaLabel={buildBenchmarkTooltipLabel(level, comparison, segmentNoun)} onMouseEnter={showBenchmarkHover} onBlur={hideTooltip} /> : null;
    $[58] = benchmarkLit;
    $[59] = benchmarkShare;
    $[60] = comparison;
    $[61] = hideTooltip;
    $[62] = level;
    $[63] = segmentNoun;
    $[64] = showBenchmark;
    $[65] = showBenchmarkHover;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div style={{
      height: powerBarLayoutBottomSpacerPx()
    }} />;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== hideTooltip || $[69] !== t18 || $[70] !== t19) {
    t21 = <div ref={barRef} className="relative" onMouseLeave={hideTooltip}>{t18}{t19}{t20}</div>;
    $[68] = hideTooltip;
    $[69] = t18;
    $[70] = t19;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className={cn("flex justify-between text-[10px] leading-none text-muted-foreground", AIFI_DATA_NUMBER_CLASS)}><span>0</span><span>100%</span></div>;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  const t23 = hoverTarget != null;
  let t24;
  if ($[73] !== tooltipRows) {
    t24 = <ChartTooltipPanel rows={tooltipRows} />;
    $[73] = tooltipRows;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== t23 || $[76] !== t24 || $[77] !== tooltipPos.x || $[78] !== tooltipPos.y) {
    t25 = <FloatingTooltip open={t23} x={tooltipPos.x} y={tooltipPos.y}>{t24}</FloatingTooltip>;
    $[75] = t23;
    $[76] = t24;
    $[77] = tooltipPos.x;
    $[78] = tooltipPos.y;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== t21 || $[81] !== t25 || $[82] !== t4) {
    t26 = <div className={t4}>{t21}{t22}{t25}</div>;
    $[80] = t21;
    $[81] = t25;
    $[82] = t4;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  return t26;
}